import * as React from 'react';
import { z } from 'zod';
import { Form, FormField, useZodForm } from './Form';
import { ReasonPicker, type ReasonPickerOption } from './Picker';
import { UrgentlyFormFrame } from './UrgentlyFormFrame';

export interface UrgentlyCompletionModalProps {
  isOpen: boolean;
  /** Whether the vehicle was towed and, only if it was, whether it was stored. */
  onSubmit: (towRequired: boolean, storageRequired: boolean) => void;
  onCancel: () => void;
  /** The completion is in flight: the controls lock and the button says so. */
  isSubmitting: boolean;
  /**
   * `sheet` rises from the bottom edge, for the driver app on a phone;
   * the dashboard keeps the dialog.
   */
  presentation?: 'dialog' | 'sheet';
}

const YES_NO_OPTIONS: ReasonPickerOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

/**
 * Completing an Urgently call: was the vehicle towed, and if so was it
 * stored. The second question only appears once the first is answered yes,
 * and answering no again clears it.
 *
 *   <UrgentlyCompletionModal isOpen={open} onSubmit={completeCall}
 *     onCancel={close} isSubmitting={saving} />
 *
 * Both answers reset to No each time the dialog opens.
 */
const FORM_ID = 'urgently-completion-form';

const EMPTY = { towRequired: 'no' as const, storageRequired: 'no' as const };

/**
 * Both answers are yes-or-no and start at No, so neither can be left blank —
 * the marker says they must be answered, which is what a choice with no
 * empty option means.
 */
export const urgentlyCompletionSchema = z.object({
  towRequired: z.enum(['yes', 'no']).default('no'),
  storageRequired: z.enum(['yes', 'no']).default('no'),
});

export function UrgentlyCompletionModal({
  isOpen,
  onSubmit,
  onCancel,
  isSubmitting,
  presentation = 'dialog',
}: UrgentlyCompletionModalProps) {
  const form = useZodForm(urgentlyCompletionSchema, { defaultValues: EMPTY });
  const [towOpen, setTowOpen] = React.useState(false);
  const [storageOpen, setStorageOpen] = React.useState(false);
  const towRequired = form.watch('towRequired');

  React.useEffect(() => {
    if (isOpen) {
      form.reset(EMPTY);
      setTowOpen(false);
      setStorageOpen(false);
    }
  }, [isOpen, form]);

  const handleCancel = () => {
    form.reset(EMPTY);
    setTowOpen(false);
    setStorageOpen(false);
    onCancel();
  };

  return (
    <UrgentlyFormFrame
      presentation={presentation}
      open={isOpen}
      onCancel={handleCancel}
      title="Complete Call"
      formId={FORM_ID}
      submitLabel={isSubmitting ? 'Completing...' : 'Complete Call'}
      isSubmitting={isSubmitting}
    >
      <Form
        form={form}
        gap={4}
        id={FORM_ID}
        className="overflow-visible"
        onSubmit={(values) => {
          const towed = values.towRequired === 'yes';
          onSubmit(towed, towed && values.storageRequired === 'yes');
        }}
      >
        <FormField form={form} name="towRequired" label="Was vehicle towed?">
          {(field) => (
            <ReasonPicker
              options={YES_NO_OPTIONS}
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                setTowOpen(false);
                // Storage only means anything for a vehicle that was towed.
                if (value === 'no') form.setValue('storageRequired', 'no');
              }}
              open={towOpen}
              onOpenChange={setTowOpen}
              disabled={isSubmitting}
              maxHeight="none"
            />
          )}
        </FormField>

        {towRequired === 'yes' && (
          <FormField
            form={form}
            name="storageRequired"
            label="Was vehicle stored?"
          >
            {(field) => (
              <ReasonPicker
                options={YES_NO_OPTIONS}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setStorageOpen(false);
                }}
                open={storageOpen}
                onOpenChange={setStorageOpen}
                disabled={isSubmitting}
                maxHeight="none"
                className="z-30"
              />
            )}
          </FormField>
        )}
      </Form>
    </UrgentlyFormFrame>
  );
}
